// Overlay UI (caméra zoom 1, lancé en parallèle des scènes de jeu). Affiche :
//   - 'show-dialogue' : une bulle simple (portrait + nom + réplique)
//   - 'show-kazi'     : une FICHE Kazi (portrait + nom + rôle + réplique + jauges d'humeur)
// Tap/clic, E ou Échap ferment (le clavier est relayé par GameScene via GlobalInfo).
// Caméra propre (zoom 1) pour que l'UI ne soit pas zoomée comme la scène de jeu.
import 'phaser'
import { SceneName } from './enums/SceneNames'
import GlobalInfo from '../GlobalInfo'
import { portraitKey, buildAppearanceSvg } from '../avatars/dicebear'
import { getMoods, MOOD_META } from '../sim/moods'
import ServerLink from '../net/ServerLink'
import { defaultAppearanceFor, type AgentPublicState } from '../net/protocol'
import { ChatPanel, type ChatInfos } from '../ui/ChatPanel'

// Per-agent conversation thread cached for the page session (persistence across reloads = Phase 2).
const threads = new Map<string, { who: 'me' | 'them'; text: string }[]>()
function cssColor(n: number | string): string {
    return typeof n === 'string' ? n : '#' + ((n >>> 0) & 0xffffff).toString(16).padStart(6, '0')
}

export interface DialoguePayload {
    displayName: string
    role: string
    dialogueHook: string
    portraitId: string
}
export interface KaziPayload {
    id: string
    displayName: string
    role: string
    dialogueHook: string
}

export default class DialogueScene extends Phaser.Scene {
    private objs: Phaser.GameObjects.GameObject[] = []
    private busy = false
    private openedAt = 0
    // --- chat (messenger) mode state ---
    private chatAgentId?: string
    private chatHandlers: Array<{ ev: string; fn: (...a: any[]) => void }> = []
    private chat?: ChatPanel

    private advance = (): void => {
        if (!this.busy) return
        if (this.time.now - this.openedAt < 200) return // ignore l'appui d'ouverture
        this.close()
    }

    // En mode chat : un clic sur la CARTE (canvas) ferme. Le panneau DOM (à droite) capte ses propres
    // clics, donc seuls les clics "hors panneau" atteignent Phaser et arrivent ici.
    private onChatTap = (): void => {
        if (!this.busy) return
        if (this.time.now - this.openedAt < 200) return
        this.close()
    }

    /** Enable/disable the gameplay scenes' keyboards (so typing doesn't leak into the game). */
    private setGameKeyboards(on: boolean): void {
        for (const s of this.scene.manager.getScenes(true)) {
            if (s.scene.key === SceneName.Dialogue) continue
            const kb = (s as any).input?.keyboard
            if (kb) kb.enabled = on
        }
    }

    constructor() {
        super(SceneName.Dialogue)
    }

    create(): void {
        GlobalInfo.on('show-dialogue', this.onShow, this)
        GlobalInfo.on('show-kazi', this.onShowKazi, this)
        this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
            GlobalInfo.off('show-dialogue', this.onShow, this)
            GlobalInfo.off('show-kazi', this.onShowKazi, this)
        })
    }

    // ouvre un panneau : gèle le joueur, branche les entrées de fermeture.
    // closeOnTap=false pour le mode conversation (les clics servent aux boutons / à la saisie).
    private beginPanel(closeOnTap = true): boolean {
        if (this.busy) return false
        this.busy = true
        GlobalInfo.emit('inDialogue', true)
        this.openedAt = this.time.now
        if (closeOnTap) {
            this.input.on('pointerdown', this.advance)
            GlobalInfo.on('dialogue-advance', this.advance)
        }
        GlobalInfo.on('dialogue-close', this.close, this)
        return true
    }

    /** Strip BBCode delimiters from untrusted text (agent lines, names) before interpolating. */
    private bb(s: string): string { return (s || '').replace(/[[\]]/g, '') }

    private track<T extends Phaser.GameObjects.GameObject>(o: T): T {
        this.objs.push(o)
        return o
    }

    // --- Bulle simple ---
    private onShow(payload: DialoguePayload): void {
        if (!this.beginPanel()) return
        const rexUI = (this as any).rexUI
        const W = this.scale.width, H = this.scale.height
        const key = portraitKey(payload.portraitId)
        const icon = this.textures.exists(key)
            ? this.add.image(0, 0, key).setDisplaySize(84, 84)
            : undefined
        const content =
            `[b][color=#90cdf4]${payload.displayName}[/color][/b]  [color=#cbd5e0]${payload.role}[/color]\n` +
            `[color=#ffffff]${payload.dialogueHook}[/color]\n` +
            `[color=#90cdf4][size=13]▼  E / tap pour fermer[/size][/color]`
        const text = rexUI.add.BBCodeText(0, 0, content, {
            fontSize: '19px', color: '#ffffff', wrap: { mode: 'word', width: 360 }, lineSpacing: 6,
        })
        const panel = rexUI.add.sizer({
            x: W / 2, y: H - 14, orientation: 'x',
            space: { left: 16, right: 18, top: 14, bottom: 14, item: 14 },
        })
        if (icon) panel.add(icon, { align: 'center' })
        panel.add(text, { align: 'center' })
        panel.setOrigin(0.5, 1).layout()
        const b = panel.getBounds()
        const bg = rexUI.add
            .roundRectangle(b.centerX, b.centerY, b.width, b.height, 14, 0x10151f, 0.98)
            .setStrokeStyle(3, 0x90cdf4).setDepth(999)
        panel.setDepth(1000)
        this.track(bg); this.track(panel)
    }

    // --- Fiche Kazi = MESSENGER (panneau DOM ancré à droite + chat multi-tours) ---
    private onShowKazi(payload: KaziPayload): void {
        if (!this.beginPanel(false)) return // mode chat : les clics servent au panneau, pas à fermer
        const id = payload.id
        this.chatAgentId = id
        const live = ServerLink.getAgent(id)
        if (live) ServerLink.queryState(id)
        const ap = live?.appearance ?? defaultAppearanceFor(id)
        // Approved photo→pixel-art portrait wins over the generated DiceBear face.
        const portraitSrc = live?.portrait
            ? ServerLink.assetUrl(live.portrait)
            : 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(buildAppearanceSvg(ap, id, 96))
        const role = live ? (live.kind === 'staff' ? 'Personnel' : 'Élève') : payload.role

        this.chat = new ChatPanel({
            displayName: payload.displayName,
            portraitSrc,
            header: { role, emotion: live?.emotion, action: live?.action },
            infos: this.buildInfos(live, id),
            quicks: ['Salut !', 'Ça va ?', 'Tu fais quoi ?', 'Tes amis ?', 'À plus'],
            closeLabel: 'À plus',
            onSend: (text) => { this.pushThread(id, 'me', text); this.chat?.appendMe(text); ServerLink.playerSay(id, text) },
            onClose: () => this.close(),
            onInputFocus: (focused) => this.setGameKeyboards(!focused),
        })

        // rejoue l'historique de session (continuité tant que l'onglet vit ; persistance serveur = phase 2)
        for (const m of threads.get(id) ?? []) m.who === 'me' ? this.chat.appendMe(m.text, true) : this.chat.appendThem(m.text, true)

        // répliques de CET agent ADRESSÉES AU JOUEUR seulement (les lignes agent→agent ont un targetAgentId)
        const onAction = (p: any): void => {
            if (p?.agentId !== id || !p?.line || p?.targetAgentId) return
            this.pushThread(id, 'them', String(p.line))
            this.chat?.appendThem(String(p.line))
        }
        GlobalInfo.on('server-agent-action', onAction)
        this.chatHandlers.push({ ev: 'server-agent-action', fn: onAction })

        // Persisted history from the server (survives reloads) — server is the source of truth.
        const onConvHistory = (p: any): void => {
            if (p?.agentId !== id) return
            const messages: { who: 'me' | 'them'; text: string }[] = (p.turns ?? []).map((t: any) => ({
                who: (t.sender === 'player' ? 'me' : 'them') as 'me' | 'them',
                text: String(t.text),
            }))
            this.chat?.setThread(messages)
            threads.set(id, messages)
        }
        GlobalInfo.on('server-conversation-history', onConvHistory)
        this.chatHandlers.push({ ev: 'server-conversation-history', fn: onConvHistory })
        ServerLink.queryConversation(id)

        // rafraîchit l'en-tête + les infos quand l'état de l'agent change
        const onState = (p: any): void => {
            if (p?.agent?.id !== id) return
            const a = ServerLink.getAgent(id)
            this.chat?.setHeaderAction(a?.action)
            this.chat?.setInfos(this.buildInfos(a, id))
        }
        GlobalInfo.on('server-agent-state', onState)
        this.chatHandlers.push({ ev: 'server-agent-state', fn: onState })

        // un clic sur la carte (hors panneau DOM) ferme
        this.input.on('pointerdown', this.onChatTap)
    }

    private buildInfos(live: AgentPublicState | undefined, id: string): ChatInfos {
        const moods = live ? live.moods : getMoods(id)
        const rows = MOOD_META.map(m => ({ label: m.label, value: Math.round((moods as any)[m.key] as number), color: cssColor((m as any).color) }))
        const rel = live?.relationships ?? []
        return {
            moods: rows,
            amis: rel.filter(r => r.type === 'ami' || r.type === 'proche').map(r => r.displayName),
            connus: rel.filter(r => r.type === 'connaissance').map(r => r.displayName),
            selfConcept: live?.selfConcept,
            aura: live?.aura,
            creations: live?.creations,
        }
    }

    private pushThread(id: string, who: 'me' | 'them', text: string): void {
        const arr = threads.get(id) ?? []
        arr.push({ who, text })
        if (arr.length > 100) arr.splice(0, arr.length - 100) // cap session memory
        threads.set(id, arr)
    }

    private close(): void {
        this.input.off('pointerdown', this.advance)
        this.input.off('pointerdown', this.onChatTap)
        GlobalInfo.off('dialogue-advance', this.advance)
        GlobalInfo.off('dialogue-close', this.close, this)
        // chat teardown
        for (const { ev, fn } of this.chatHandlers) GlobalInfo.off(ev, fn)
        this.chatHandlers = []
        this.chat?.destroy(); this.chat = undefined
        this.setGameKeyboards(true) // always hand the keyboard back to the game
        this.chatAgentId = undefined
        this.objs.forEach(o => o.destroy())
        this.objs = []
        this.busy = false
        GlobalInfo.emit('inDialogue', false)
    }
}
